import { FC, useContext, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
  showWarningNotification,
} from "../../context/notificationContext";
import { usePageContext } from "../../context/pageContext";
import { usePermissionsContext } from "../../context/permissionContext";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import useToggle from "../../hooks/useToggle";
import { useI18n } from "../../hooks/useTranslations";
import useItemApi from "../../http/item/api";
import { InsertItem, Item } from "../../http/item/types";
import { PaginationQuery } from "../../types";
import { formatPrice, getItemDescription } from "../../utils";
import SvgAdd from "../icons/Add";
import SvgDelete from "../icons/Delete";
import BlueCheck from "../ui/BlueCheck";
import Button from "../ui/Button";
import { Action } from "../ui/ListItemMenu";
import ModalDialog from "../ui/ModalDialog";
import RedCross from "../ui/RedCross";
import Sidebar from "../ui/Sidebar";
import Table from "../ui/Table";
import TH from "../ui/TH";
import ItemForm, { ItemFormBody } from "./ItemForm";

const Items: FC = () => {
  const itemsApi = useItemApi();

  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());
  const [itemToShow, setItemToShow] = useState<Item>();
  const [itemToDelete, setItemToDelete] = useState<Item>();
  const { open, close, isOpen } = useToggle();
  const permissions = usePermissionsContext();
  const [isModalSubmitting, setIsModalSubmitting] = useState(false);
  const [searchParams] = useSearchParams();

  const pageContext = usePageContext();

  const query: PaginationQuery = { ...filters.state, q: debouncedValue };

  const { data, error } = itemsApi.useItems(query);

  useEffect(() => {
    pageContext.setPage({ title: i18n.t.items.plural });
  }, [i18n.language]);

  const itemId = searchParams.get("id");
  const item = itemsApi.useItem(itemId ?? "");

  useEffect(() => {
    itemId && item.refetch();
  }, [itemId]);

  useEffect(() => {
    item.data?.id && setItemToShow(item.data);
  }, [item.data?.id]);

  useEffect(() => {
    const apiError = error ?? item.error;

    if (!apiError) return;

    dispatch(showErrorNotification(i18n, apiError));
  }, [error, item.error]);

  const closeShowItemDialog = () => {
    setItemToShow(undefined);
    if (itemId) {
      const url = new URL(window.location.href);
      url.searchParams.delete("id");
      window.history.replaceState({}, document.title, url.toString());
    }
  };

  const filterOptions = {
    get: itemsApi.getFieldFiltersQuery,
    update: filters.setFilters,
    getSelectedOptions: filters.getFilters,
  };

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const onSubmit = async (formData: ItemFormBody) => {
    try {
      setIsModalSubmitting(true);
      const insertData: InsertItem = {
        id: formData.item?.id!,
        organizationId: formData.organization?.id!,
      };

      await itemsApi.createItem(insertData);
      close();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsModalSubmitting(false);
    }
  };

  const actions: Action<Item>[] = [
    {
      title: i18n.t.common.delete,
      onClick: setItemToDelete,
      icon: SvgDelete,
      color: "red",
    },
  ];

  return (
    <>
      <Sidebar isOpen={isOpen} onClose={close} title={i18n.t.items.newItem}>
        <ItemForm
          onSubmit={onSubmit}
          key={isOpen ? (itemToShow?.id ?? "new") : "closed"}
          isLoading={isModalSubmitting}
        />
      </Sidebar>
      <Table<Item>
        buttons={
          permissions.isWarehouseOperator || permissions.isPurchaseOffice
            ? {
                main: (
                  <Button
                    buttonProps={{
                      onClick: open,
                    }}
                    glyph={SvgAdd}
                  />
                ),
              }
            : undefined
        }
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        filters={filters}
        head={
          <tr>
            <TH fieldName="item.code" order={orderOptions} title={i18n.t.common.code} />
            <th align="left"> {i18n.t.common.description}</th>
            <TH fieldName="item.isKit" order={orderOptions} filters={filterOptions} title={i18n.t.common.isKit} />
            <TH fieldName="item.cost" order={orderOptions} title={i18n.t.common.cost} />
            <th></th>
          </tr>
        }
        renderRow={(item) => (
          <>
            <td>{item.code}</td>
            <td>{getItemDescription(item, i18n.language)}</td>
            <td align="left">{item.isKit ? <BlueCheck /> : <RedCross />}</td>
            <td>{formatPrice(item.cost, i18n.language)}</td>
          </>
        )}
        onRowClick={setItemToShow}
        items={data?.items}
        total={data?.total}
        error={error}
        rowActions={actions}
      />
      <Sidebar isOpen={!!itemToShow} onClose={closeShowItemDialog} title={itemToShow?.code ?? ""}>
        {itemToShow && (
          <>
            <ItemForm
              item={{
                ...itemToShow,
              }}
              isDisabled
              isLoading={isModalSubmitting}
              onSync={
                permissions.isWarehouseOperator || permissions.isPurchaseOffice
                  ? async () => {
                      try {
                        setIsModalSubmitting(true);
                        const updatedItem = await itemsApi.syncItem(itemToShow.id);
                        updatedItem && setItemToShow(updatedItem);
                        dispatch(showSuccessNotification(i18n.t, i18n.t.common.dataSyncedSuccessfully));
                      } catch (error) {
                        dispatch(showErrorNotification(i18n, error));
                      } finally {
                        setIsModalSubmitting(false);
                      }
                    }
                  : undefined
              }
              onDelete={
                permissions.isWarehouseOperator || permissions.isPurchaseOffice
                  ? () => setItemToDelete(itemToShow)
                  : undefined
              }
            ></ItemForm>
          </>
        )}
      </Sidebar>
      {!!itemToDelete && (
        <ModalDialog
          title={i18n.t.items.delete}
          isSubmitting={isModalSubmitting}
          onClose={() => setItemToDelete(undefined)}
          actions={
            !itemToDelete?.isKit
              ? [
                  {
                    title: i18n.t.common.cancel,
                    type: "secondary",
                    onClick: () => setItemToDelete(undefined),
                  },
                  {
                    title: i18n.t.common.yes,

                    onClick: async () => {
                      if (!itemToDelete) return;
                      try {
                        setIsModalSubmitting(true);
                        await itemsApi.deleteItem(itemToDelete.id);
                        setItemToDelete(undefined);
                        setIsModalSubmitting(false);
                      } catch (e) {
                        setIsModalSubmitting(false);
                        dispatch(showErrorNotification(i18n, e));
                      }
                    },
                  },
                ]
              : [
                  {
                    title: i18n.t.common.cancel,
                    type: "secondary",
                    onClick: () => setItemToDelete(undefined),
                  },
                  {
                    title: i18n.t.items.deleteJustKit,

                    onClick: async () => {
                      if (!itemToDelete) return;
                      try {
                        setIsModalSubmitting(true);
                        await itemsApi.deleteItem(itemToDelete.id);
                        setItemToDelete(undefined);
                        setIsModalSubmitting(false);
                      } catch (e) {
                        setIsModalSubmitting(false);
                        dispatch(showErrorNotification(i18n, e));
                      }
                    },
                  },
                  {
                    title: i18n.t.items.deleteKitAndComponents,

                    onClick: async () => {
                      if (!itemToDelete) return;
                      try {
                        setIsModalSubmitting(true);
                        const allDeleted = await itemsApi.deleteItem(itemToDelete.id, true);
                        if (!allDeleted) {
                          dispatch(showWarningNotification(i18n.t.warnings.notAllComponentsDeleted));
                        }
                        setItemToDelete(undefined);
                        setIsModalSubmitting(false);
                      } catch (e) {
                        setIsModalSubmitting(false);
                        dispatch(showErrorNotification(i18n, e));
                      }
                    },
                  },
                ]
          }
        >
          {itemToDelete?.isKit ? i18n.t.items.questions.deleteComponents : i18n.t.items.questions.delete}
          {itemToDelete?.isKit ?? <b>{i18n.t.items.deleteComponentsInfo}</b>}
        </ModalDialog>
      )}
    </>
  );
};

export default Items;
